/**
 * Accordion tools.
 */
H_Tools.accordions = (() => {
    /**
     * Closing duration of accordions. This should be same as CSS transition duration.
     */
    const closingDuration = 500;

    /**
     * Prevent the supplied accordion button from animating.
     *
     * @param {*} button An accordion button
     */
    function preventAnimation(button) {
        button.classList.add('-do-not-animate');
        setTimeout(
            (button) => {
                button.classList.remove('-do-not-animate');
            },
            closingDuration,
            button
        );
    }

    /**
     * Prevent the supplied accordion button from animating if the button is not in the visible
     * viewport.
     *
     * @param {*} button An accordion button
     */
    function preventAnimationIfNotVisible(button) {
        if (H_Tools?.stickyheader?.isElementVisible(button)) {
            return;
        }

        preventAnimation(button);
    }

    /**
     * Closes the accordion item associated with the supplied button.
     *
     * @param {*} button An accordion button
     */
    function closeAccordion(button) {
        button.setAttribute('aria-expanded', 'false');
        if (button.id) {
            localStorage.removeItem('accordion_state_' + button.id);
        }
    }

    /**
     * Closes all accordion items.
     */
    function closeAllAccordions() {
        const accordion_buttons = document.querySelectorAll(
            '.js-accordion-button'
        );

        accordion_buttons.forEach((button) => {
            preventAnimationIfNotVisible(button);
            closeAccordion(button);
        });
    }

    /**
     * Checks whether single item mode is enabled for the supplied button. Returns a representative
     * boolean value.
     *
     * Single item mode = whether or not only a single accordion item should be allowed open at a
     * given time. Currently single item mode depends on sticky header tools.
     *
     * @param {*} button An accordion button
     */
    function isSingleItemMode(button) {
        return (
            H_Tools?.stickyheader &&
            button.getAttribute('data-single-item-mode-disabled') !== 'true'
        );
    }

    /**
     * Opens the accordion item associated with the supplied button.
     *
     * @param {*} button An accordion button
     */
    function openAccordion(button) {
        button.setAttribute('aria-expanded', 'true');
        if (button.id) {
            localStorage.setItem('accordion_state_' + button.id, 'open');
        }
    }

    /**
     * Immediately scroll to the supplied accordion button.
     *
     * @param {*}       button      An accordion button
     * @param {boolean} forceScroll Whether to scroll even if the button is already visible
     */
    function scrollToAccordion(button, forceScroll = false) {
        if (!forceScroll && H_Tools?.stickyheader?.isElementVisible(button)) {
            return;
        }
        H_Tools?.stickyheader?.scrollTo(button, false);
    }

    /**
     * Focuses an accordion button without triggering the sticky header backward-focus scroll.
     *
     * @param {*} button An accordion button
     */
    function focusAccordionButton(button) {
        button.classList.add('js-backwards-focus-ignore');
        button.focus({ preventScroll: true });
        button.classList.remove('js-backwards-focus-ignore');
    }

    /**
     * Toggle a single accordion item.
     *
     * @param {*} button An accordion button
     */
    function toggleAccordion(button) {
        const isClosed = button.getAttribute('aria-expanded') !== 'true';

        if (isClosed) {
            if (isSingleItemMode(button)) {
                closeAllAccordions();
            }
            openAccordion(button);
        } else {
            closeAccordion(button);
        }

        if (isSingleItemMode(button)) {
            scrollToAccordion(button);
        }
    }

    /**
     * Handles opening/scrolling to an accordion when the page is loaded or when navigating via an
     * anchor link (#hash in URL).
     *
     * @param {*}       el            An element
     * @param {boolean} shouldAnimate If false, disables accordion animation when opening
     */
    function handleAnchorLinks(el, shouldAnimate = false) {
        // console.log('Handling anchor links...');

        // get the hash from the URL (e.g. "#heading-foo" or an accordion button's own id)
        const hash = window.location.hash;

        if (!hash) {
            return;
        }

        /**
         * Find any element matching the hash - either an accordion button itself, or a heading
         * nested inside one - ignoring hidden copies inside a link collection's filter data store.
         */
        const targetElement = Array.from(el.querySelectorAll(hash)).find(
            (element) => !element.closest('.js-link-collection-link-store')
        );

        if (!targetElement) {
            return;
        }

        /**
         * Resolve to the accordion button. The hash may have matched the button itself, the
         * heading the button sits inside, or - in layouts that nest the other way around - a
         * heading nested inside the button.
         */
        const targetButton =
            targetElement.closest('.js-accordion-button') ||
            targetElement.querySelector('.js-accordion-button');

        if (!targetButton) {
            return;
        }

        /**
         * If we are navigating to a new accordion, close others first but disable their animations
         * to prevent "jumping" layout shifts.
         */
        if (
            isSingleItemMode(targetButton) &&
            targetButton.getAttribute('aria-expanded') !== 'true'
        ) {
            const accordion_buttons = document.querySelectorAll(
                '.js-accordion-button'
            );
            accordion_buttons.forEach((button) => {
                if (button !== targetButton) {
                    // Force no animation on closing others to stabilize layout immediately
                    button.classList.add('-do-not-animate');
                    closeAccordion(button);
                    setTimeout(
                        () => button.classList.remove('-do-not-animate'),
                        10
                    );
                }
            });
        }

        // Open the target
        if (!shouldAnimate) {
            preventAnimation(targetButton);
        }
        openAccordion(targetButton);

        // Scroll immediately.
        // Using requestAnimationFrame ensures the DOM has updated from the 'open' state
        requestAnimationFrame(() => {
            scrollToAccordion(targetButton, true);
            focusAccordionButton(targetButton);
        });
    }

    /**
     * Makes all accordions inside the supplied element open and close when clicked.
     *
     * This function may need to be reaplied if the DOM is manipulated using JavaScript.
     *
     * @param {*} el An element
     */
    function enableAccordions(el) {
        const accordion_buttons = el.querySelectorAll(
            // target all accordion buttons except those inside link collection link stores
            '.js-accordion-button:not(.js-link-collection-link-store .js-accordion-button)'
        );

        accordion_buttons.forEach((button) => {
            /**
             * Prevent adding the listener more than once by checking and setting an expando
             * property on the element.
             *
             * @see https://developer.mozilla.org/en-US/docs/Glossary/Expando
             */
            if (button.hasAccordionListener) {
                return;
            }
            button.hasAccordionListener = true;

            button.addEventListener('click', function () {
                toggleAccordion(this);
            });
        });

        handleAnchorLinks(el, false);
    }

    /**
     * Restores the state of all accordions inside the supplied element.
     *
     * @param {*} el An element
     */
    function restoreAccordionState(el) {
        // console.log('Restoring accordion state...');

        const accordion_buttons = el.querySelectorAll('.js-accordion-button');
        accordion_buttons.forEach((button) => {
            if (button.id) {
                if (localStorage.getItem('accordion_state_' + button.id)) {
                    preventAnimation(button);
                    openAccordion(button);
                }
            }
        });
    }

    // return an object to expose inner functions
    return {
        enableAccordions: enableAccordions,
        restoreAccordionState: restoreAccordionState,
        handleAnchorLinks: handleAnchorLinks,
    };
})();
